import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAccountOpeningStep } from "../../redux/accountOpening";
import "./UploadDocumentDialog.css";
import { SubmitSwiftDocsPayload } from "../../models/IEnums.model";
import { useFormik } from "formik";
import "react-html5-camera-photo/build/css/index.css";
import { initialSwiftDocs } from "../../utils/constants";
import RadioBtn from "../../assets/icons/RadioBtn";
import DocUpload from "../DocUpload";
import apiHooks from "../../customHooks/apis";
import UploadSignatureCard from "../UploadSignatureCard";
import CustomModal from "../Modal/CustomModal";
import SignaturePad from "../SignaturePad";
import { extractValuesFromObj } from "../../utils/helpers";
import { submitSwiftDocsValidationSchema } from "../../utils/validationSchemas";
import useUserValidated from "../../customHooks/useUserValidated";
import ImageUploadAndLivePhoto from "../ImageUploadAndLivePhoto";
import { string } from "yup";

function UploadDocumentSwiftSavingsDialog(props: any) {
  const dispatch = useDispatch();
  const { useUploadFile, useUploadSwiftDocs, useUserDetails } = apiHooks;
  const { userDetails, handleUpdateUserDetails } = useUserDetails();
  const { handleUploadFile, uploadingFile } = useUploadFile();
  const { handleUploadSwiftDocs, uploadingDocs } = useUploadSwiftDocs();
  const { isValid } = useUserValidated();
  const [isUploading, setIsUploading] = useState(false);
  const formik = useFormik<SubmitSwiftDocsPayload>({
    initialValues: initialSwiftDocs,
    validationSchema: submitSwiftDocsValidationSchema.shape({
      imageUrl: isValid
        ? string().notRequired()
        : string().required("Image is required"),
    }),
    onSubmit: handleUploadSwiftDocs,
  });
  const [docUrl, setDocUrl] = useState<{ drawn: string; uploaded: string }>({
    drawn: "",
    uploaded: "",
  });
  const [eSignature, setESignature] = useState<string>("");
  const [showSignaturePad, setShowSignaturePad] = useState<boolean>(false);
  const validationType = localStorage.getItem("validationType");

  const goToStep = (step: any) => {
    handleUpdateUserDetails(formik.values);
    if (step === "update-detail") {
      localStorage.setItem("previous-screen", "document-upload");
    }
    dispatch(setAccountOpeningStep(step));
  };

  const radioBtnState = (state: boolean) =>
    typeof formik.values.debitCardValueAddedService !== "string" && state;

  const handleUploadESignature = (
    postedFilebase64: string,
    documentName: string,
    filetype: string,
    objectUrl: string,
  ) => {
    handleUploadFile({ postedFilebase64, documentName, filetype }, (drawn) =>
      setDocUrl({ ...docUrl, drawn }),
    );
    setESignature(objectUrl);
    setShowSignaturePad(false);
  };

  const handleRemoveESignature = (drawn: string) => {
    setESignature(drawn);
    setDocUrl((prevUrl) => ({ ...prevUrl, drawn }));
    formik.setFieldValue("signatureUrl", "");
  };

  const handleTouchSignature = () =>
    formik.setFieldTouched("signatureUrl", true);

  useEffect(() => {
    const accTypeFromStorage = localStorage.getItem("altFinanceAccountType");
    const glCodeFromStorage = localStorage.getItem("altFinanceGlCode");
    formik.setValues({
      ...initialSwiftDocs,
      ...extractValuesFromObj(initialSwiftDocs, userDetails),
      accountTypecode: glCodeFromStorage || "",
      accountTypeName: accTypeFromStorage || "",
      imageUrl: localStorage.getItem("userImage") || "",
      signatureUrl: "",
    });
  }, [userDetails]);

  useEffect(() => {
    const signatureUrl = docUrl.drawn || docUrl.uploaded;
    if (signatureUrl) {
      formik.setValues({
        ...formik.values,
        signatureUrl,
      });
    }
  }, [docUrl]);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className=" card-form pl-1 pr-1 ">
          <div className="card-body">
            <h4 className="card-title text-center pl-3 mt-5">
              Document upload
            </h4>
            <p className="text-center">
              <small>
                The file format should be JPEG, PNG or PDF (max size:2mb)
              </small>
            </p>

            <div className="row mr-3">
              <div className="d-flex flex-column flex-lg-row align-items-end gap-2">
                <div
                  className={`${
                    validationType === "bvn" ? "w-100" : "w-lg-50 w-100"
                  } flex-column flex-lg-row d-flex align-items-end gap-2`}
                >
                  <div className="w-lg-50 w-100">
                    <DocUpload
                      required
                      disabled={uploadingFile}
                      show={!docUrl.drawn}
                      getFileUrl={(_, uploaded) => {
                        setDocUrl({ ...docUrl, uploaded });
                        handleTouchSignature();
                      }}
                      label={docUrl.uploaded ? "" : "Draw/Upload Signature"}
                      errorMessage={
                        formik.touched.signatureUrl
                          ? formik.errors.signatureUrl
                          : ""
                      }
                      setIsUploading={setIsUploading}
                    />
                  </div>
                  <div className="w-lg-50 w-100">
                    <UploadSignatureCard
                      imageUrl={eSignature}
                      show={!docUrl.uploaded}
                      onClickCard={() => {
                        setShowSignaturePad(true);
                        handleTouchSignature();
                      }}
                      uploading={uploadingFile}
                      handleRemoveFile={handleRemoveESignature}
                      disabled={isUploading}
                    />
                  </div>
                </div>
                <ImageUploadAndLivePhoto
                  getFileUrl={(url) => formik.setFieldValue("imageUrl", url)}
                />
              </div>
              <div className="d-flex flex-column gap-2  w-auto mt-4">
                <small className="fw-medium">
                  Would you like to have a Debit Card (Verve)
                </small>
                <div className="w-100 d-flex justify-content-between">
                  <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                    <RadioBtn
                      color="#181717"
                      checked={radioBtnState(
                        !!formik.values.debitCardValueAddedService,
                      )}
                      onClickCheck={() =>
                        formik.handleChange({
                          target: {
                            name: "debitCardValueAddedService",
                            value: true,
                          },
                        })
                      }
                    />
                    Yes
                  </span>
                  <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                    <RadioBtn
                      color="#181717"
                      checked={radioBtnState(
                        !formik.values.debitCardValueAddedService,
                      )}
                      onClickCheck={() =>
                        formik.handleChange({
                          target: {
                            name: "debitCardValueAddedService",
                            value: false,
                          },
                        })
                      }
                    />
                    No
                  </span>
                </div>
              </div>
              <CustomModal
                show={showSignaturePad}
                onClickClose={() => setShowSignaturePad(false)}
              >
                <SignaturePad onClickSave={handleUploadESignature} />
              </CustomModal>
              {/* ------------------------ */}
            </div>
          </div>

          <div className="d-flex justify-content-end mt-2">
            <div className="mb-3 mr-4" style={{ marginRight: "20px" }}>
              <button
                type="button"
                onClick={() => {
                  goToStep("update-detail");
                }}
                className="btn btn-outline-gold mr-3 btn-filled-white"
              >
                Previous
              </button>
            </div>
            {uploadingDocs ? (
              <div className="spinner-border text-gold" role="status">
                <span className="sr-only"></span>
              </div>
            ) : (
              <button
                type="submit"
                className="btn btn-filled-red"
                disabled={!formik.isValid}
              >
                Complete
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default UploadDocumentSwiftSavingsDialog;
